import React, { useEffect } from 'react';
import { includes, map, keys, range, isEmpty, capitalize } from 'lodash';
import classnames from 'classnames';
import Toggle from '../../../toggle';
import Wysiwyg from '../../../../../../common/components/wysiwyg';
import InlineDropdown from '../../../inlineDropdown';
import InfoToolTip from '../../../infoToolTip';
import {
  abilityField, abilityNField, imageViewVerticalField, showStimuliField
} from './fields';

const defaultDisplayNumberForSingle = 1;
const minSelectedAnswersToDisplay = 0;

const OpenEnd = ({ allowReasons, conceptLabel, customMetrics = false, question, questions, setQuestions }) => {
  const singleKind = question.kind === 'Single';
  const displayAbility = (
    allowReasons &&
      includes([ 'Single', 'Multi' ], question.kind) &&
      !question.as_point_scale
  );
  const askReason = question[abilityField];
  const displayN = parseInt(question[abilityNField]);
  const imageViewVertical = question[imageViewVerticalField];
  const showStimuli = question[showStimuliField];
  const numberOfAnswers = keys(question.answers).length + (question.other_answer ? 1 : 0);
  const handleToggleAskReason = (value) => {
    question[abilityField] = value;
    if (value && !question.reasons?.length) {
      if (!question.reasons) {
        question.reasons = [];
      }
      question.reasons.push({ text: '' });
    }
    setQuestions([ ...questions ]);
  };

  const handleToggleImageViewChange = (value) => {
    question[imageViewVerticalField] = value;
    setQuestions([ ...questions ]);
  };

  const handleToggleShowStimuliChange = (value) => {
    question[showStimuliField] = value;
    setQuestions([ ...questions ]);
  };

  const hasErrorText = question.errors && question.errors['reasons.text'];

  const handleChangeText = (newHtml) => {
    question.reasons[0].text = newHtml;
    setQuestions([ ...questions ]);
  };

  const handleChangeN = (value) => {
    question[abilityNField] = value;
    setQuestions([ ...questions ]);
  };

  const generatedRange = (
    singleKind ?
      [ 0, 1 ] :
      range(minSelectedAnswersToDisplay, numberOfAnswers + 1) // end is not included
  );

  const selectedNumTitle = (num) => (
    num ? `${num} selected item${num > 1 ? 's' : ''}` : 'no selected items'
  );

  useEffect(() => {
    if (!displayN && displayN !== 0) {
      handleChangeN(
        singleKind ?
          defaultDisplayNumberForSingle :
          numberOfAnswers
      );
    }
  }, []);

  useEffect(() => {
    if (!displayAbility && askReason) {
      handleToggleAskReason(false);
    }
    const maxNum = generatedRange[generatedRange.length - 1];
    if (maxNum < displayN) {
      handleChangeN(maxNum);
    }
  }, [ questions ]);

  useEffect(() => {
    if (typeof question[imageViewVerticalField] !== 'boolean') {
      question[imageViewVerticalField] = true;
    }
  }, []);

  const hasImages = !isEmpty(question?.answer_images);

  return (
    <>
      <div className="form_section -image-kind">
        {
          hasImages &&
          <Toggle
            onChange={ handleToggleImageViewChange }
            checked={ imageViewVertical }
            hint={
              <>
                Turning off this setting will change the orientation to a
                { ' ' }
                horizontal one in desktop view.
              </>
            }
          >
            Vertical orientation in desktop view.
          </Toggle>
        }
      </div>
      {
        customMetrics && displayAbility && askReason &&
        <>
          <div className="form_section -display-concept">
            <Toggle
              onChange={ handleToggleShowStimuliChange }
              checked={ showStimuli }
              hint={
                <>
                  {capitalize(conceptLabel)} will be displayed for base question and for open-end.
                  { ' ' }
                  Also options will be displayed as for ordinary question.
                </>
              }
            >
              Display {capitalize(conceptLabel)}
            </Toggle>
          </div>
        </>
      }
      {
        displayAbility &&
        <div className="reason-open-end">
          <Toggle
            onChange={ handleToggleAskReason }
            checked={ askReason }
            hint={ <>Respondent will be asked about the reasons of their selection</> }
          >
            Include Reasons Question
          </Toggle>
          <div className={ classnames("reason-open-end-details", { '-expand': askReason }) }>
            <div className="reason-open-end-details-inner">
              <div className={ classnames("form_section-line", { 'has-error': hasErrorText }) }>
                <Wysiwyg
                  customPicker
                  toolbarClassName="wysiwyg-menu"
                  editorClassName="wysiwyg-textarea"
                  placeholder="Add"
                  enableIndent
                  value={ question.reasons?.length ? question.reasons[0].text : '' }
                  onChange={ handleChangeText }
                />
              </div>
              <div className="form_section -multi-options">
                Display
                { !displayN || singleKind ? ' ' : ' at most'  }
                {' '}
                <InlineDropdown
                  value={ displayN }
                  onChange={ handleChangeN }
                  list={
                    map(generatedRange, (num) => (
                      { title: selectedNumTitle(num), value: num }
                    ))
                  }
                />
                <InfoToolTip
                  remote
                  text={
                    <>
                      Maximum number of selected options that will be displayed
                      {' '}
                      along with the "Reasons Question".
                    </>
                  }
                  position="top-right"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default OpenEnd;
